import { useGetSystemLossThresholdsHistoryQuery } from '@/services/systemLossThresholds';
import type { SystemLossThresholdsHistoryDiff } from '@/services/systemLossThresholds';
import { DateTime } from 'luxon';

const COUNTY_OVERRIDES_SEGMENT = 'countyOverrides';

const FIELD_LABELS: Record<string, string> = {
  snowLossPercent: 'snow loss',
  minSystemLossPercent: 'min system loss',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const formatDiffValue = (value: unknown): string => {
  if (value === undefined) return '—';
  if (value === null) return 'none';
  if (typeof value === 'number') return `${value}%`;
  if (isRecord(value)) {
    const parts = [
      value.snowLossPercent !== undefined ? `snow ${formatDiffValue(value.snowLossPercent)}` : undefined,
      value.minSystemLossPercent !== undefined ? `min ${formatDiffValue(value.minSystemLossPercent)}` : undefined,
      Array.isArray(value.countyOverrides) ? `${value.countyOverrides.length} county override(s)` : undefined,
    ].filter((part): part is string => part !== undefined);
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
};

// Paths are state-relative: TX, TX/snowLossPercent, NY/countyOverrides/Kings[/field]. The
// leading "value/" is stripped defensively in case the raw event path is passed through.
// A whole-county add/remove carries the override object, whose name is the admin's spelling.
export const describeDiff = (diff: SystemLossThresholdsHistoryDiff): string => {
  const segments = diff.path.replace(/^value\//, '').split('/');
  const [stateCode, second, countySegment, field] = segments;
  const isAdd = diff.from === undefined && diff.to !== undefined;
  const isRemoval = diff.to === undefined && diff.from !== undefined;

  if (second === undefined) {
    if (isAdd) return `Added state ${stateCode} (${formatDiffValue(diff.to)})`;
    if (isRemoval) return `Removed state ${stateCode} (${formatDiffValue(diff.from)})`;
    return `${stateCode}: ${formatDiffValue(diff.from)} → ${formatDiffValue(diff.to)}`;
  }

  if (second === COUNTY_OVERRIDES_SEGMENT && countySegment !== undefined) {
    const overrideName = isRecord(diff.to) ? diff.to.name : isRecord(diff.from) ? diff.from.name : undefined;
    const county = typeof overrideName === 'string' ? overrideName : countySegment;
    if (field === undefined) {
      if (isAdd) return `Added ${stateCode} county override ${county} (${formatDiffValue(diff.to)})`;
      if (isRemoval) return `Removed ${stateCode} county override ${county} (${formatDiffValue(diff.from)})`;
      return `${stateCode} ${county}: ${formatDiffValue(diff.from)} → ${formatDiffValue(diff.to)}`;
    }
    return `${stateCode} ${county} ${FIELD_LABELS[field] ?? field}: ${formatDiffValue(diff.from)} → ${formatDiffValue(diff.to)}`;
  }

  if (second === COUNTY_OVERRIDES_SEGMENT) {
    return `${stateCode} county overrides: ${formatDiffValue(diff.from)} → ${formatDiffValue(diff.to)}`;
  }

  return `${stateCode} ${FIELD_LABELS[second] ?? second}: ${formatDiffValue(diff.from)} → ${formatDiffValue(diff.to)}`;
};

const isNoOpDiff = (diff: SystemLossThresholdsHistoryDiff) => JSON.stringify(diff.from) === JSON.stringify(diff.to);

export const ChangeHistory = () => {
  const { data, isLoading, isError } = useGetSystemLossThresholdsHistoryQuery();

  const entries = (data?.data ?? [])
    .map((entry) => ({
      ...entry,
      diffs: entry.diffs.filter((diff) => !isNoOpDiff(diff)),
    }))
    .filter((entry) => entry.diffs.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-bold">Change History</h3>
      {isLoading ? (
        <p className="text-sm text-text-secondary">Loading...</p>
      ) : entries.length === 0 && isError ? (
        <p className="text-sm text-text-error">History unavailable.</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-text-secondary">No changes recorded yet.</p>
      ) : (
        <>
          {entries.map((entry) => (
            <div
              key={`${entry.timestamp}-${entry.actorEmail ?? 'system'}`}
              data-testid="history-entry"
              className="flex flex-col gap-2 rounded-6 bg-canvas-highlight-default p-16"
            >
              <div className="text-xs text-text-tertiary">
                {DateTime.fromISO(entry.timestamp).toFormat('MMM d, yyyy, h:mm a')} by {entry.actorEmail ?? 'system'}
              </div>
              <ul className="list-disc pl-16 text-sm">
                {entry.diffs.map((diff) => (
                  <li key={diff.path}>{describeDiff(diff)}</li>
                ))}
              </ul>
            </div>
          ))}
          {!!data && data.total > data.data.length && (
            <p className="text-xs text-text-tertiary">
              Showing latest {data.data.length} of {data.total} changes
            </p>
          )}
        </>
      )}
    </div>
  );
};
